import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CdkDrag, CdkDragPlaceholder, CdkDropList } from '@angular/cdk/drag-drop';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { AutoCompleteModule } from '@openng/optimus-ui/autocomplete';
import { ButtonModule } from '@openng/optimus-ui/button';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { SelectModule } from '@openng/optimus-ui/select';
import { RadioButtonModule } from '@openng/optimus-ui/radiobutton';
import { ToastModule } from '@openng/optimus-ui/toast';

// Shared chrome components
import { AddBlockComponent } from '../common/add-block/add-block.component';
import { HandleComponent } from '../common/handle/handle.component';
import { HeartbeatComponent } from '../common/heartbeat/heartbeat.component';
import { GoogleRsaPreviewComponent } from '../types/google-templates/rsa-preview/rsa-preview.component';

// Email block components
import { SubjectComponent } from '../blocks/email-blocks/subject/subject.component';
import { PreheaderComponent } from '../blocks/email-blocks/preheader/preheader.component';
import { CounterComponent } from '../blocks/email-blocks/counter/counter.component';
import { MainBannerComponent } from '../blocks/email-blocks/main-banner/main-banner.component';
import { VoucherComponent } from '../blocks/email-blocks/voucher/voucher.component';
import { GiftVoucherComponent } from '../blocks/email-blocks/gift-voucher/gift-voucher.component';
import { ParagraphCtaComponent } from '../blocks/email-blocks/paragraph-cta/paragraph-cta.component';
import { HeadingComponent } from '../blocks/email-blocks/heading/heading.component';
import { OnlyIntroComponent } from '../blocks/email-blocks/only-intro/only-intro.component';
import { OnlyCtaComponent } from '../blocks/email-blocks/only-cta/only-cta.component';
import { DisclaimerComponent } from '../blocks/email-blocks/disclaimer/disclaimer.component';
import { Only2PhotosComponent } from '../blocks/email-blocks/only-2-photos/only-2-photos.component';
import { PropertyUsp1xPhotoComponent } from '../blocks/email-blocks/property-usp-1x-photo/property-usp-1x-photo.component';
import { PropertyUsp2xPhotoComponent } from '../blocks/email-blocks/property-usp-2x-photo/property-usp-2x-photo.component';
import { PropertyDescriptionComponent } from '../blocks/email-blocks/property-description/property-description.component';
import { X2PropRecComponent } from '../blocks/email-blocks/x2-prop-rec/x2-prop-rec.component';
import { X2PropRecOnpComponent } from '../blocks/email-blocks/x2-prop-rec-onp/x2-prop-rec-onp.component';
import { ReviewsComponent } from '../blocks/reviews/reviews.component';
import { ZigZagNoHeadingNoButtonComponent } from '../blocks/email-blocks/zigzag-no-heading-no-button/zigzag-no-heading-no-button';
import { ZigZagHeadingAndCtaComponent } from '../blocks/email-blocks/zigzag-heading-and-cta/zigzag-heading-and-cta';
import { ZigZagHeadingAndAltCtaComponent } from '../blocks/email-blocks/zigzag-heading-and-alt-cta/zigzag-heading-and-alt-cta';
import { InspirationComponent } from '../blocks/email-blocks/inspiration/inspiration.component';
import { EventComponent } from '../blocks/email-blocks/event/event.component';
import { X2ExperiencesComponent } from '../blocks/email-blocks/x2-experiences/x2-experiences.component';
import { X3ExperiencesComponent } from '../blocks/email-blocks/x3-experiences/x3-experiences.component';
import { MomentsComponent } from '../blocks/email-blocks/moments/moments.component';
import { ReservationComponent } from '../blocks/email-blocks/reservation/reservation.component';
import { ConciergeComponent } from '../blocks/email-blocks/concierge/concierge.component';
import { CheckInComponent } from '../blocks/email-blocks/check-in/check-in.component';
import { ValamarAppComponent } from '../blocks/email-blocks/valamar-app/valamar-app.component';

// Google RSA search block components
import { HeadlineComponent } from '../blocks/google-blocks/headline/headline.component';
import { DescriptionComponent } from '../blocks/google-blocks/description/description.component';
import { RsaSearchImageComponent } from '../blocks/google-blocks/rsa-search-image/rsa-search-image.component';

// Single-channel block components
import { SmsComponent } from '../blocks/sms-blocks/sms.component';
import { WhatsappComponent } from '../blocks/whatsapp-blocks/whatsapp.component';
import { PushComponent } from '../blocks/push-blocks/push.component';
import { WebPushComponent } from '../blocks/web-push-blocks/web-push.component';
import { RcsComponent } from '../blocks/rcs-blocks/rcs.component';
import { ViberComponent } from '../blocks/viber-blocks/viber.component';
import { SitelinkComponent } from '../blocks/google-blocks/sitelink/sitelink.component';
import { GoogleRsaSearchFinalUrlComponent } from '../blocks/google-blocks/rsa-search-final-url/rsa-search-final-url.component';
import { GoogleRsaSearchDisplayPathComponent } from '../blocks/google-blocks/rsa-search-display-path/rsa-search-display-path.component';
import { GoogleRsaSearchCallsComponent } from '../blocks/google-blocks/rsa-search-calls/rsa-search-calls.component';
import { RsaSearchCalloutsComponent } from '../blocks/google-blocks/rsa-search-callouts/rsa-search-callouts.component';
import { GenSingleImageHeadlineComponent } from '../blocks/google-blocks/gen-single-image-headline/gen-single-image-headline.component';
import { GenSingleImageDescriptionComponent } from '../blocks/google-blocks/gen-single-image-description/gen-single-image-description.component';
import { GenSingleImageImageComponent } from '../blocks/google-blocks/gen-single-image-image/gen-single-image-image.component';
import { GenSingleImageUrlComponent } from '../blocks/google-blocks/gen-single-image-url/gen-single-image-url.component';
import { GenSingleImageCtaTextComponent } from '../blocks/google-blocks/gen-single-image-cta-text/gen-single-image-cta-text.component';
import { GenSingleImageLogoComponent } from '../blocks/google-blocks/gen-single-image-logo/gen-single-image-logo.component';
import { GenSingleImagePreviewComponent } from '../types/google-templates/gen-single-image-preview/gen-single-image-preview.component';
import { GenVideoLongHeadlineComponent } from '../blocks/google-blocks/gen-video-long-headline/gen-video-long-headline.component';
import { GenVideoHeadlineComponent } from '../blocks/google-blocks/gen-video-headline/gen-video-headline.component';
import { GenVideoDescriptionComponent } from '../blocks/google-blocks/gen-video-description/gen-video-description.component';
import { GenVideoYoutubeUrlComponent } from '../blocks/google-blocks/gen-video-youtube-url/gen-video-youtube-url.component';
import { GenVideoUrlComponent } from '../blocks/google-blocks/gen-video-url/gen-video-url.component';
import { GenVideoCtaTextComponent } from '../blocks/google-blocks/gen-video-cta-text/gen-video-cta-text.component';
import { GenVideoPreviewComponent } from '../types/google-templates/gen-video-preview/gen-video-preview.component';
import { PmaxLongHeadlineComponent } from '../blocks/google-blocks/pmax-long-headline/pmax-long-headline.component';
import { PmaxHeadlineComponent } from '../blocks/google-blocks/pmax-headline/pmax-headline.component';
import { PmaxDescriptionComponent } from '../blocks/google-blocks/pmax-description/pmax-description.component';
import { PmaxImageComponent } from '../blocks/google-blocks/pmax-image/pmax-image.component';
import { PmaxYoutubeUrlComponent } from '../blocks/google-blocks/pmax-youtube-url/pmax-youtube-url.component';
import { PmaxUrlComponent } from '../blocks/google-blocks/pmax-url/pmax-url.component';
import { PmaxCtaTextComponent } from '../blocks/google-blocks/pmax-cta-text/pmax-cta-text.component';
import { PmaxSitelinkComponent } from '../blocks/google-blocks/pmax-sitelink/pmax-sitelink.component';
import { PmaxPreviewComponent } from '../types/google-templates/pmax-preview/pmax-preview.component';
import { YoutubeVideoHeadlineComponent } from '../blocks/google-blocks/youtube-video-headline/youtube-video-headline.component';
import { YoutubeVideoYoutubeUrlComponent } from '../blocks/google-blocks/youtube-video-youtube-url/youtube-video-youtube-url.component';
import { YoutubeVideoUrlComponent } from '../blocks/google-blocks/youtube-video-url/youtube-video-url.component';
import { YoutubeVideoCtaTextComponent } from '../blocks/google-blocks/youtube-video-cta-text/youtube-video-cta-text.component';
import { YoutubeVideoSitelinkComponent } from '../blocks/google-blocks/youtube-video-sitelink/youtube-video-sitelink.component';
import { YoutubeVideoPreviewComponent } from '../types/google-templates/youtube-video-preview/youtube-video-preview.component';
import { MetaSingleImageHeadlineComponent } from '../blocks/meta-blocks/meta-single-image-headline/meta-single-image-headline.component';
import { MetaSingleImagePrimaryTextComponent } from '../blocks/meta-blocks/meta-single-image-primary-text/meta-single-image-primary-text.component';
import { MetaSingleImageCtaTextComponent } from '../blocks/meta-blocks/meta-single-image-cta-text/meta-single-image-cta-text.component';
import { MetaSingleImageUrlComponent } from '../blocks/meta-blocks/meta-single-image-url/meta-single-image-url.component';
import { MetaSingleImageAccountTypeComponent } from '../blocks/meta-blocks/meta-single-image-account-type/meta-single-image-account-type.component';
import { MetaSingleImageImageComponent } from '../blocks/meta-blocks/meta-single-image-image/meta-single-image-image.component';
import { MetaSingleImagePreviewComponent } from '../types/meta-templates/meta-single-image-preview/meta-single-image-preview.component';
import { MetaVideoHeadlineComponent } from '../blocks/meta-blocks/meta-video-headline/meta-video-headline.component';
import { MetaVideoPrimaryTextComponent } from '../blocks/meta-blocks/meta-video-primary-text/meta-video-primary-text.component';
import { MetaVideoCtaTextComponent } from '../blocks/meta-blocks/meta-video-cta-text/meta-video-cta-text.component';
import { MetaVideoUrlComponent } from '../blocks/meta-blocks/meta-video-url/meta-video-url.component';
import { MetaVideoAccountTypeComponent } from '../blocks/meta-blocks/meta-video-account-type/meta-video-account-type.component';
import { MetaVideoVideoComponent } from '../blocks/meta-blocks/meta-video-video/meta-video-video.component';
import { MetaVideoPreviewComponent } from '../types/meta-templates/meta-video-preview/meta-video-preview.component';
import { MetaCarouselPrimaryTextComponent } from '../blocks/meta-blocks/meta-carousel-primary-text/meta-carousel-primary-text.component';
import { MetaCarouselCtaTextComponent } from '../blocks/meta-blocks/meta-carousel-cta-text/meta-carousel-cta-text.component';
import { MetaCarouselAccountTypeComponent } from '../blocks/meta-blocks/meta-carousel-account-type/meta-carousel-account-type.component';
import { MetaCarouselCardComponent } from '../blocks/meta-blocks/meta-carousel-card/meta-carousel-card.component';
import { MetaCarouselPreviewComponent } from '../types/meta-templates/meta-carousel-preview/meta-carousel-preview.component';

/**
 * Union of everything the shared `template-editor.component.html` references.
 * Every channel editor (types/* and translate/*) spreads this into its
 * `@Component({ imports })` so the one shared template compiles in each
 * standalone component's context.
 */
export const TEMPLATE_EDITOR_IMPORTS = [
  CommonModule,
  FormsModule,
  ReactiveFormsModule,
  CdkDropList,
  CdkDrag,
  CdkDragPlaceholder,
  NgbTooltip,
  AutoCompleteModule,
  ButtonModule,
  ConfirmDialogModule,
  SelectModule,
  RadioButtonModule,
  ToastModule,
  AddBlockComponent,
  HandleComponent,
  HeartbeatComponent,
  GoogleRsaPreviewComponent,
  GenSingleImagePreviewComponent,
  GenVideoPreviewComponent,
  PmaxPreviewComponent,
  YoutubeVideoPreviewComponent,
  MetaSingleImagePreviewComponent,
  MetaVideoPreviewComponent,
  MetaCarouselPreviewComponent,
  // Email blocks
  SubjectComponent,
  PreheaderComponent,
  CounterComponent,
  MainBannerComponent,
  VoucherComponent,
  GiftVoucherComponent,
  ParagraphCtaComponent,
  HeadingComponent,
  OnlyIntroComponent,
  OnlyCtaComponent,
  DisclaimerComponent,
  Only2PhotosComponent,
  PropertyUsp1xPhotoComponent,
  PropertyUsp2xPhotoComponent,
  PropertyDescriptionComponent,
  X2PropRecComponent,
  X2PropRecOnpComponent,
  ReviewsComponent,
  ZigZagNoHeadingNoButtonComponent,
  ZigZagHeadingAndCtaComponent,
  ZigZagHeadingAndAltCtaComponent,
  InspirationComponent,
  EventComponent,
  X2ExperiencesComponent,
  X3ExperiencesComponent,
  MomentsComponent,
  ReservationComponent,
  ConciergeComponent,
  CheckInComponent,
  ValamarAppComponent,
  // Google RSA search blocks
  HeadlineComponent,
  DescriptionComponent,
  RsaSearchImageComponent,
  SitelinkComponent,
  GoogleRsaSearchFinalUrlComponent,
  GoogleRsaSearchDisplayPathComponent,
  GoogleRsaSearchCallsComponent,
  RsaSearchCalloutsComponent,
  // Google Demand Gen Single Image
  GenSingleImageHeadlineComponent,
  GenSingleImageDescriptionComponent,
  GenSingleImageImageComponent,
  GenSingleImageUrlComponent,
  GenSingleImageCtaTextComponent,
  GenSingleImageLogoComponent,
  // Google Demand Gen Video
  GenVideoLongHeadlineComponent,
  GenVideoHeadlineComponent,
  GenVideoDescriptionComponent,
  GenVideoYoutubeUrlComponent,
  GenVideoUrlComponent,
  GenVideoCtaTextComponent,
  // Google Performance Max
  PmaxLongHeadlineComponent,
  PmaxHeadlineComponent,
  PmaxDescriptionComponent,
  PmaxImageComponent,
  PmaxYoutubeUrlComponent,
  PmaxUrlComponent,
  PmaxCtaTextComponent,
  PmaxSitelinkComponent,
  // Google YouTube Video Ad
  YoutubeVideoHeadlineComponent,
  YoutubeVideoYoutubeUrlComponent,
  YoutubeVideoUrlComponent,
  YoutubeVideoCtaTextComponent,
  YoutubeVideoSitelinkComponent,
  // Meta Single Image Ad
  MetaSingleImageHeadlineComponent,
  MetaSingleImagePrimaryTextComponent,
  MetaSingleImageCtaTextComponent,
  MetaSingleImageUrlComponent,
  MetaSingleImageAccountTypeComponent,
  MetaSingleImageImageComponent,
  MetaVideoHeadlineComponent,
  MetaVideoPrimaryTextComponent,
  MetaVideoCtaTextComponent,
  MetaVideoUrlComponent,
  MetaVideoAccountTypeComponent,
  MetaVideoVideoComponent,
  // Meta Carousel Ad
  MetaCarouselPrimaryTextComponent,
  MetaCarouselCtaTextComponent,
  MetaCarouselAccountTypeComponent,
  MetaCarouselCardComponent,
  // Single-channel blocks
  SmsComponent,
  WhatsappComponent,
  PushComponent,
  WebPushComponent,
  RcsComponent,
  ViberComponent,
] as const;
